export type DeploymentType = 'dev' | 'preview' | 'prod_lite' | 'prod';
export type AppType = 'static' | 'backend';
export type PlanType = 'shared' | 'dedicated';
export type NetworkMode = 'shared' | 'tenant';
export type ComputeMode = 'shared' | 'dedicated';
export type PlanSize = 'nano' | 'micro' | 'small' | 'medium' | null;

export interface PlanTranslationKeys {
  name: string;
  title?: string;
  subtitle?: string;
  description?: string;
  badge?: string;
  badgeSubtitle?: string;
  traffic?: string;
  trafficMessage?: string;
  features: string[];
}

export interface PlanConfig {
  id: string;
  translationKeys: PlanTranslationKeys;
  price: number | null;
  appType: AppType;
  deploymentType: DeploymentType;
  planType: PlanType;
  networkMode: NetworkMode;
  computeMode: ComputeMode;
  planSize: PlanSize;
}

export const DEPLOY_APP_PLANS_CONFIG: PlanConfig[] = [
  // Static Plans
  {
    id: 'static-dev',
    translationKeys: {
      name: 'deployApp.plans.staticDev.name',
      title: 'deployApp.plans.staticDev.title',
      subtitle: 'deployApp.plans.staticDev.subtitle',
      description: 'deployApp.plans.staticDev.description',
      badge: 'deployApp.plans.staticDev.badge',
      traffic: 'deployApp.plans.staticDev.traffic',
      trafficMessage: 'deployApp.plans.staticDev.trafficMessage',
      features: [
        'deployApp.plans.staticDev.feature1',
        'deployApp.plans.staticDev.feature2',
        'deployApp.plans.staticDev.feature3',
        'deployApp.plans.staticDev.feature4'
      ]
    },
    price: 5,
    appType: 'static',
    deploymentType: 'dev',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: null
  },
  {
    id: 'static-preview',
    translationKeys: {
      name: 'deployApp.plans.staticPreview.name',
      title: 'deployApp.plans.staticPreview.title',
      subtitle: 'deployApp.plans.staticPreview.subtitle',
      description: 'deployApp.plans.staticPreview.description',
      badge: 'deployApp.plans.staticPreview.badge',
      traffic: 'deployApp.plans.staticPreview.traffic',
      trafficMessage: 'deployApp.plans.staticPreview.trafficMessage',
      features: [
        'deployApp.plans.staticPreview.feature1',
        'deployApp.plans.staticPreview.feature2',
        'deployApp.plans.staticPreview.feature3',
        'deployApp.plans.staticPreview.feature4',
        'deployApp.plans.staticPreview.feature5'
      ]
    },
    price: 8,
    appType: 'static',
    deploymentType: 'preview',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: null
  },
  {
    id: 'static-prod-lite',
    translationKeys: {
      name: 'deployApp.plans.staticProdLite.name',
      title: 'deployApp.plans.staticProdLite.title',
      subtitle: 'deployApp.plans.staticProdLite.subtitle',
      description: 'deployApp.plans.staticProdLite.description',
      badge: 'deployApp.plans.staticProdLite.badge',
      badgeSubtitle: 'deployApp.plans.staticProdLite.badgeSubtitle',
      traffic: 'deployApp.plans.staticProdLite.traffic',
      trafficMessage: 'deployApp.plans.staticProdLite.trafficMessage',
      features: [
        'deployApp.plans.staticProdLite.feature1',
        'deployApp.plans.staticProdLite.feature2',
        'deployApp.plans.staticProdLite.feature3',
        'deployApp.plans.staticProdLite.feature4',
        'deployApp.plans.staticProdLite.feature5'
      ]
    },
    price: 12,
    appType: 'static',
    deploymentType: 'prod_lite',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: null
  },
  {
    id: 'static-prod',
    translationKeys: {
      name: 'deployApp.plans.staticProd.name',
      title: 'deployApp.plans.staticProd.title',
      description: 'deployApp.plans.staticProd.description',
      badge: 'deployApp.plans.staticProd.badge',
      traffic: 'deployApp.plans.staticProd.traffic',
      trafficMessage: 'deployApp.plans.staticProd.trafficMessage',
      features: [
        'deployApp.plans.staticProd.feature1',
        'deployApp.plans.staticProd.feature2',
        'deployApp.plans.staticProd.feature3',
        'deployApp.plans.staticProd.feature4',
        'deployApp.plans.staticProd.feature5'
      ]
    },
    price: null,
    appType: 'static',
    deploymentType: 'prod',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: null
  },
  
  // Backend Plans - Dev
  {
    id: 'dev-shared-nano',
    translationKeys: {
      name: 'deployApp.plans.devSharedNano.name',
      features: [
        'deployApp.plans.devSharedNano.feature1',
        'deployApp.plans.devSharedNano.feature2',
        'deployApp.plans.devSharedNano.feature3',
        'deployApp.plans.devSharedNano.feature4',
        'deployApp.plans.devSharedNano.feature5'
      ]
    },
    price: 9,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: 'nano'
  },
  {
    id: 'dev-shared-micro',
    translationKeys: {
      name: 'deployApp.plans.devSharedMicro.name',
      features: [
        'deployApp.plans.devSharedMicro.feature1',
        'deployApp.plans.devSharedMicro.feature2'
      ]
    },
    price: 15,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: 'micro'
  },
  {
    id: 'dev-shared-small',
    translationKeys: {
      name: 'deployApp.plans.devSharedSmall.name',
      features: [
        'deployApp.plans.devSharedSmall.feature1'
      ]
    },
    price: 25,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: 'small'
  },
  {
    id: 'dev-assigned-nano',
    translationKeys: {
      name: 'deployApp.plans.devAssignedNano.name',
      features: [
        'deployApp.plans.devAssignedNano.feature1',
        'deployApp.plans.devAssignedNano.feature2'
      ]
    },
    price: 12,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'nano'
  },
  {
    id: 'dev-assigned-micro',
    translationKeys: {
      name: 'deployApp.plans.devAssignedMicro.name',
      features: [
        'deployApp.plans.devAssignedMicro.feature1',
        'deployApp.plans.devAssignedMicro.feature2'
      ]
    },
    price: 20,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'micro'
  },
  {
    id: 'dev-assigned-small',
    translationKeys: {
      name: 'deployApp.plans.devAssignedSmall.name',
      features: [
        'deployApp.plans.devAssignedSmall.feature1',
        'deployApp.plans.devAssignedSmall.feature2'
      ]
    },
    price: 35,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'small'
  },
  {
    id: 'dev-tenant-dedicated-micro',
    translationKeys: {
      name: 'deployApp.plans.devTenantDedicatedMicro.name',
      features: [
        'deployApp.plans.devTenantDedicatedMicro.feature1',
        'deployApp.plans.devTenantDedicatedMicro.feature2',
        'deployApp.plans.devTenantDedicatedMicro.feature3',
        'deployApp.plans.devTenantDedicatedMicro.feature4'
      ]
    },
    price: 18,
    appType: 'backend',
    deploymentType: 'dev',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: 'micro'
  },
  
  // Backend Plans - Preview
  {
    id: 'preview-micro',
    translationKeys: {
      name: 'deployApp.plans.previewMicro.name',
      features: [
        'deployApp.plans.previewMicro.feature1',
        'deployApp.plans.previewMicro.feature2',
        'deployApp.plans.previewMicro.feature3',
        'deployApp.plans.previewMicro.feature4',
        'deployApp.plans.previewMicro.feature5'
      ]
    },
    price: 20,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: 'micro'
  },
  {
    id: 'preview-small',
    translationKeys: {
      name: 'deployApp.plans.previewSmall.name',
      features: [
        'deployApp.plans.previewSmall.feature1',
        'deployApp.plans.previewSmall.feature2',
        'deployApp.plans.previewSmall.feature3'
      ]
    },
    price: 35,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'shared',
    networkMode: 'shared',
    computeMode: 'shared',
    planSize: 'small'
  },
  
  // Backend Plans - Preview: Red Compartida + Cómputo Dedicado
  {
    id: 'preview-shared-dedicated-micro',
    translationKeys: {
      name: 'deployApp.plans.previewSharedDedicatedMicro.name',
      features: [
        'deployApp.plans.previewSharedDedicatedMicro.feature1',
        'deployApp.plans.previewSharedDedicatedMicro.feature2',
        'deployApp.plans.previewSharedDedicatedMicro.feature3',
        'deployApp.plans.previewSharedDedicatedMicro.feature4'
      ]
    },
    price: 28,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'micro'
  },
  {
    id: 'preview-shared-dedicated-small',
    translationKeys: {
      name: 'deployApp.plans.previewSharedDedicatedSmall.name',
      features: [
        'deployApp.plans.previewSharedDedicatedSmall.feature1',
        'deployApp.plans.previewSharedDedicatedSmall.feature2',
        'deployApp.plans.previewSharedDedicatedSmall.feature3',
        'deployApp.plans.previewSharedDedicatedSmall.feature4'
      ]
    },
    price: 45,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'small'
  },
  {
    id: 'preview-shared-dedicated-medium',
    translationKeys: {
      name: 'deployApp.plans.previewSharedDedicatedMedium.name',
      features: [
        'deployApp.plans.previewSharedDedicatedMedium.feature1',
        'deployApp.plans.previewSharedDedicatedMedium.feature2',
        'deployApp.plans.previewSharedDedicatedMedium.feature3',
        'deployApp.plans.previewSharedDedicatedMedium.feature4'
      ]
    },
    price: 65,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'shared',
    computeMode: 'dedicated',
    planSize: 'medium'
  },
  
  // Backend Plans - Preview: Red Dedicada + Cómputo Dedicado
  {
    id: 'preview-tenant-dedicated-micro',
    translationKeys: {
      name: 'deployApp.plans.previewTenantDedicatedMicro.name',
      features: [
        'deployApp.plans.previewTenantDedicatedMicro.feature1',
        'deployApp.plans.previewTenantDedicatedMicro.feature2',
        'deployApp.plans.previewTenantDedicatedMicro.feature3',
        'deployApp.plans.previewTenantDedicatedMicro.feature4'
      ]
    },
    price: 38,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: 'micro'
  },
  {
    id: 'preview-tenant-dedicated-small',
    translationKeys: {
      name: 'deployApp.plans.previewTenantDedicatedSmall.name',
      features: [
        'deployApp.plans.previewTenantDedicatedSmall.feature1',
        'deployApp.plans.previewTenantDedicatedSmall.feature2',
        'deployApp.plans.previewTenantDedicatedSmall.feature3',
        'deployApp.plans.previewTenantDedicatedSmall.feature4'
      ]
    },
    price: 58,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: 'small'
  },
  {
    id: 'preview-tenant-dedicated-medium',
    translationKeys: {
      name: 'deployApp.plans.previewTenantDedicatedMedium.name',
      features: [
        'deployApp.plans.previewTenantDedicatedMedium.feature1',
        'deployApp.plans.previewTenantDedicatedMedium.feature2',
        'deployApp.plans.previewTenantDedicatedMedium.feature3',
        'deployApp.plans.previewTenantDedicatedMedium.feature4'
      ]
    },
    price: 85,
    appType: 'backend',
    deploymentType: 'preview',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: 'medium'
  },
  
  // Backend Plans - Prod Lite
  {
    id: 'prod-lite',
    translationKeys: {
      name: 'deployApp.plans.prodLite.name',
      features: [
        'deployApp.plans.prodLite.feature1',
        'deployApp.plans.prodLite.feature2',
        'deployApp.plans.prodLite.feature3',
        'deployApp.plans.prodLite.feature4',
        'deployApp.plans.prodLite.feature5'
      ]
    },
    price: 45,
    appType: 'backend',
    deploymentType: 'prod_lite',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: null
  },
  
  // Backend Plans - Prod
  {
    id: 'prod-ha',
    translationKeys: {
      name: 'deployApp.plans.prodHA.name',
      features: [
        'deployApp.plans.prodHA.feature1',
        'deployApp.plans.prodHA.feature2',
        'deployApp.plans.prodHA.feature3',
        'deployApp.plans.prodHA.feature4',
        'deployApp.plans.prodHA.feature5'
      ]
    },
    price: null,
    appType: 'backend',
    deploymentType: 'prod',
    planType: 'dedicated',
    networkMode: 'tenant',
    computeMode: 'dedicated',
    planSize: null
  }
];

// Funciones helper
export const getPlansByFilters = (
  appType: AppType,
  deploymentType: DeploymentType,
  networkMode?: NetworkMode,
  computeMode?: ComputeMode
): PlanConfig[] => {
  return DEPLOY_APP_PLANS_CONFIG.filter(plan => {
    const matchesAppType = plan.appType === appType;
    const matchesDeploymentType = plan.deploymentType === deploymentType;
    const matchesNetworkMode = !networkMode || plan.networkMode === networkMode;
    const matchesComputeMode = !computeMode || plan.computeMode === computeMode;
    
    return matchesAppType && matchesDeploymentType && matchesNetworkMode && matchesComputeMode;
  });
};

export const getPlanById = (planId: string): PlanConfig | undefined => {
  return DEPLOY_APP_PLANS_CONFIG.find(plan => plan.id === planId);
};

export const getNetworkAndComputeModeFromPlan = (
  planId: string
): { networkMode: NetworkMode; computeMode: ComputeMode } | null => {
  const plan = getPlanById(planId);
  if (!plan) return null;
  return {
    networkMode: plan.networkMode,
    computeMode: plan.computeMode
  };
};

export const planConfigToComponentFormat = (
  plan: PlanConfig, 
  t: (key: string) => string
) => {
  return {
    id: plan.id,
    name: t(plan.translationKeys.name),
    price: plan.price,
    ...(plan.translationKeys.title && { title: t(plan.translationKeys.title) }),
    ...(plan.translationKeys.subtitle && { subtitle: t(plan.translationKeys.subtitle) }),
    ...(plan.translationKeys.description && { description: t(plan.translationKeys.description) }),
    ...(plan.translationKeys.badge && { badge: t(plan.translationKeys.badge) }),
    ...(plan.translationKeys.badgeSubtitle && { badgeSubtitle: t(plan.translationKeys.badgeSubtitle) }),
    ...(plan.translationKeys.traffic && { traffic: t(plan.translationKeys.traffic) }),
    ...(plan.translationKeys.trafficMessage && { trafficMessage: t(plan.translationKeys.trafficMessage) }),
    features: plan.translationKeys.features.map(key => t(key))
  };
};

